import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import QuestionAudioPlayer from '../shared/QuestionAudioPlayer';

// Get base URL for audio
const getBaseURL = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const MultipleCorrectAnswer = ({ question, onAnswer, onNext, sessionData = null, variant = 'default' }) => {
  const activeAnnotationRef = useRef(null);
  const annotationAudioRef = useRef(null);
  // Transform backend data to component format
  const transformedQuestion = useMemo(() => {
    const options = [];

    if (question.choices && Array.isArray(question.choices)) {
      // JANGAN SORT - gunakan urutan asli dari backend
      // Urutan choices dari backend harus dipertahankan
      question.choices.forEach((choice) => {
        options.push({
          id: choice.idx !== undefined ? choice.idx : choice.id,
          text: choice['option-text'] || choice.text || '',
          isCorrect: choice.correct === true,
        });
      });
    }

    // Handle text-soal with newlines - convert \n to <br />
    const questionText = question.text || question['text-soal'] || '';
    const formattedText = questionText.replace(/\n/g, '');

    return {
      ...question,
      options: options,
      text: formattedText,
    };
  }, [question]);

  const [selectedAnswers, setSelectedAnswers] = useState([]);

  // Reset state when question changes
  useEffect(() => {
    setSelectedAnswers([]);
    if (annotationAudioRef.current) {
      annotationAudioRef.current.pause();
      annotationAudioRef.current = null;
    }
  }, [question['soal-id'] || question.id]);

  // Build annotations map from question.annotations array
  const annotationsMap = useMemo(() => {
    const map = {};
    
    // First, try to get annotations from question.annotations array
    if (question.annotations && Array.isArray(question.annotations)) {
      question.annotations.forEach((annotation) => {
        const annId = annotation['ann-id'] || annotation.annId || annotation.id;
        const streamUrl = annotation['stream-url'] || annotation.streamUrl || annotation.stream_url;
        const annType = annotation['ann-type'] || annotation.annType || annotation.type;
        
        // If streamUrl exists, treat it as audio annotation (for new format without ann-type)
        if (annId && streamUrl) {
          map[annId] = {
            annId,
            streamUrl,
            annType: annType || 'audio',
          };
        }
      });
    }
    
    // Also parse HTML for data-ann-id attributes (fallback)
    const html = transformedQuestion.text || '';
    if (html && typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');

        nodes.forEach((node) => {
          const annId = node.getAttribute('data-ann-id');
          const annPath = node.getAttribute('data-ann-path');
          const annType = node.getAttribute('data-ann-type');

          // Only add if not already in map (from annotations array)
          // If annPath exists, treat it as audio (for new format)
          if (annId && annPath && !map[annId]) {
            // Convert data-ann-path to streamUrl
            const baseURL = getBaseURL();
            const streamUrl = `${baseURL}/${annPath}`;
            
            map[annId] = {
              annId,
              streamUrl,
              annType: annType || 'audio',
            };
          }
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('MultipleCorrectAnswer: failed to parse annotations from HTML', error);
        }
      }
    }

    return map;
  }, [question.annotations, transformedQuestion.text]);

  const questionLevelAudioUrl = question?.['audio-url'] || question?.audio_url || null;

  // Extract first annotation audio URL only when HTML text actually has [data-ann-id] nodes
  const firstAnnotationAudioUrl = useMemo(() => {
    const html = transformedQuestion.text || '';
    if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return null;
    try {
      const doc = new window.DOMParser().parseFromString(html, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');
      for (const node of nodes) {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]?.streamUrl) {
          return annotationsMap[annId].streamUrl;
        }
      }
    } catch {}
    return null;
  }, [transformedQuestion.text, annotationsMap]);

  const displayAudioUrl = questionLevelAudioUrl || firstAnnotationAudioUrl;

  // Enhance HTML with annotation classes; hide annotation shown via waveform player
  const enhancedHtml = useMemo(() => {
    const html = transformedQuestion.text || '';
    if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return html;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          const isShownAsWaveform = !questionLevelAudioUrl && annotationsMap[annId].streamUrl === firstAnnotationAudioUrl;
          if (isShownAsWaveform) {
            node.style.display = 'none';
            // Remove trailing <br> / empty text nodes so they don't render as phantom gap
            let next = node.nextSibling;
            while (next && (next.nodeName === 'BR' || (next.nodeType === 3 && next.textContent.trim() === ''))) {
              const toRemove = next;
              next = next.nextSibling;
              toRemove.parentNode.removeChild(toRemove);
            }
          } else {
            node.classList.add('practice-annotation');
            node.classList.add('annotation-hidden');
            if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '0');
            if (!node.hasAttribute('role')) node.setAttribute('role', 'button');
            node.setAttribute('aria-label', 'Play audio');
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('MultipleCorrectAnswer: failed to enhance HTML', error);
      }
      return html;
    }
  }, [transformedQuestion.text, annotationsMap, questionLevelAudioUrl, firstAnnotationAudioUrl]);

  const highlightAnnotation = (element) => {
    if (!element) return;

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('practice-annotation-active');
  };

  const handlePlayAnnotation = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    if (!streamUrl) return;

    if (!annotationAudioRef.current) {
      annotationAudioRef.current = new Audio();
      annotationAudioRef.current.addEventListener('ended', () => {
        if (activeAnnotationRef.current) {
          activeAnnotationRef.current.classList.remove('practice-annotation-active');
          activeAnnotationRef.current = null;
        }
      });
    }
    const audioEl = annotationAudioRef.current;
    audioEl.pause();
    audioEl.src = streamUrl;
    audioEl.currentTime = 0;
    audioEl.play().catch(() => {});
  };

  const findAnnotationTrigger = (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return target.closest('[data-ann-id]');
    }
    return null;
  };

  const handleAnnotationTrigger = (event) => {
    if (event.target.closest('[data-mc-option]')) return;

    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    if (!annId || !annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    handlePlayAnnotation(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  const handleOptionToggle = (optionId) => {
    setSelectedAnswers(prev => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId);
      } else {
        return [...prev, optionId];
      }
    });
  };

  const handleSubmit = () => {
    if (selectedAnswers.length === 0) return;
    
    // selectedAnswers berisi idx dari choices (bukan index array)
    // Pastikan yang dikirim adalah idx dari choices
    const correctAnswers = transformedQuestion.options
      .filter(opt => opt.isCorrect)
      .map(opt => opt.id) // opt.id adalah idx dari choices
      .sort();
    const userAnswers = [...selectedAnswers].sort(); // selectedAnswers sudah berisi idx dari choices
    
    const correct = 
      correctAnswers.length === userAnswers.length &&
      correctAnswers.every((id, index) => id === userAnswers[index]);
    
    if (process.env.NODE_ENV === 'development') {
      // console.log('🔍 MultipleCorrectAnswer - Submitting answer:', {
      //   selectedAnswers,
      //   userAnswers,
      //   correctAnswers,
      //   correct,
      //   options: transformedQuestion.options.map(opt => ({ id: opt.id, text: opt.text, isCorrect: opt.isCorrect })),
      // });
    }
    
    if (onAnswer) {
      onAnswer({
        questionId: question['soal-id'] || question.id,
        answer: selectedAnswers, // Array of idx dari choices (bukan index array)
        isCorrect: correct
      });
    }
    
    if (onNext) {
      onNext();
    }
  };

  const badgeGradient = variant === 'drill'
    ? 'from-[#345d8a] to-[#1e3d6f]'
    : 'from-[#12766d] to-[#236463]';

  return (
    <motion.div
      className="flex flex-col gap-5"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.span
          className={`px-3.5 py-2 bg-gradient-to-br ${badgeGradient} text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1`}
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Multiple Correct Answer
        </motion.span>
      </div>
      
      <div className="flex flex-col gap-5">
        {displayAudioUrl && (
          <QuestionAudioPlayer audioUrl={displayAudioUrl} />
        )}
        <div 
          className="question-text text-lg font-semibold text-[var(--text-primary)] leading-relaxed whitespace-pre-line"
          onClick={annotationsMap && Object.keys(annotationsMap).length > 0 ? handleAnnotationTrigger : undefined}
          onKeyDown={annotationsMap && Object.keys(annotationsMap).length > 0 ? handleAnnotationKeyDown : undefined}
          dangerouslySetInnerHTML={{ __html: enhancedHtml }}
        />
        
        <div className="flex flex-col gap-3">
          {transformedQuestion.options.map((option) => {
            const isSelected = selectedAnswers.includes(option.id);
            return (
              <div
                key={option.id}
                data-mc-option
                className={`option-item flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 bg-[var(--card-bg)] border-[rgba(18,118,109,0.2)] dark:border-[var(--border-color)] hover:border-[#12766d] hover:bg-[rgba(18,118,109,0.05)] hover:translate-x-1 dark:hover:border-[#14b8a6] dark:hover:bg-[rgba(20,184,166,0.08)] max-md:p-3.5 max-md:min-h-[44px] ${
                  isSelected
                    ? '!border-[#12766d] !bg-[rgba(18,118,109,0.1)] dark:!border-[#14b8a6] dark:!bg-[rgba(20,184,166,0.12)]'
                    : ''
                }`}
                onClick={() => handleOptionToggle(option.id)}
              >
                <div
                  className={`option-checkbox flex items-center justify-center w-5 h-5 min-w-[20px] shrink-0 rounded border-2 transition-colors max-md:w-[22px] max-md:h-[22px] max-md:min-w-[22px] ${
                    isSelected
                      ? 'bg-[var(--accent-teal)] border-[var(--accent-teal)]'
                      : 'bg-[var(--bg-tertiary)] border-[var(--border-color)]'
                  }`}
                >
                  {isSelected && <span className="text-white text-sm font-bold leading-none">✓</span>}
                </div>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleOptionToggle(option.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="sr-only"
                  aria-hidden
                />
                <span className="flex-1 text-base text-[var(--text-primary)] leading-normal max-md:text-[15px]">
                  {option.text}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-center pt-4 border-t-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)] max-md:pt-3 max-md:pb-20 max-md:flex-col max-md:gap-3">
        <motion.button
          className="py-3 px-8 bg-gradient-to-br from-[#fcb319] to-[#f5a500] text-[var(--text-primary)] border-none rounded-xl text-base font-semibold cursor-pointer transition-all duration-300 shadow-[0_2px_4px_rgba(252,179,25,0.2)] hover:enabled:from-[#f5a500] hover:enabled:to-[#e69500] hover:enabled:shadow-[0_4px_8px_rgba(252,179,25,0.3)] hover:enabled:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed dark:text-[#1d4746] max-md:w-full max-md:py-3.5 max-md:px-6 max-md:text-[15px] max-md:min-h-[44px]"
          onClick={handleSubmit}
          disabled={selectedAnswers.length === 0}
          whileTap={{ scale: 0.96 }}
        >
          Submit
        </motion.button>
      </div>
    </motion.div>
  );
};

export default MultipleCorrectAnswer;

